import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import { adsFromResponse, pickForZone } from './adData';

/**
 * One ad from ralkage/flarum-ext-ad-management, on a page it knows nothing about.
 *
 * That extension renders its own zones on core pages only, and its
 * `.AdZonePlaceholder` divs are filled from CommentPost -- a custom route
 * reaches neither. So we ask the same public endpoint it does,
 * `/advertisements/active`, and reuse its markup, CSS classes and tracking
 * endpoints. Nothing of theirs is touched, so it stays updatable from upstream.
 *
 * The zone is an ordinary zone of position `custom` ("does not auto-render"),
 * so the same ad does not also appear in a fixed page position.
 *
 * ponytail: extension disabled, zone empty, every ad expired -> the request
 * 404s or comes back without our zone, view() returns null, and the page is
 * byte for byte what it is today. No reserved box, no layout shift, no CSS.
 */

let pending = null;

function activeAds() {
  if (!pending) {
    pending = app
      .request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/advertisements/active',
        errorHandler: () => {},
      })
      .then(adsFromResponse)
      // no ads is a fine outcome; a broken page is not
      .catch(() => []);
  }

  return pending;
}

function track(route, attributes) {
  app
    .request({
      method: 'POST',
      url: app.forum.attribute('apiUrl') + route,
      body: { data: { attributes } },
      errorHandler: () => {},
    })
    .catch(() => {});
}

export default class AdSlot extends Component {
  oninit(vnode) {
    super.oninit(vnode);

    this.ad = null;

    // The `noAds` permission -- staff, donors, whoever the admin gives it to.
    if (app.forum.attribute('adsHidden')) return;

    activeAds().then((ads) => {
      // Picked once per visit, not in view(): Mithril redraws are global, so
      // re-picking there would swap the banner on every click in the app.
      this.ad = pickForZone(ads, this.attrs.zone);
      if (!this.ad) return;

      if (app.forum.attribute('adsTrackImpressions')) {
        track('/ad-track/impression', { adIds: [this.ad.id] });
      }

      m.redraw();
    });
  }

  view() {
    const ad = this.ad;
    if (!ad) return null;

    const showLabel = app.forum.attribute('adsShowSponsoredLabel') !== false;

    return (
      <div className={'AdZone AdZone--' + this.attrs.zone}>
        <div className={'AdBanner AdBanner--' + (ad.type || 'image')} data-ad-id={ad.id}>
          {ad.type === 'image' ? this.image(ad) : this.markup(ad)}
          {showLabel && (
            <div className="AdBanner-label">
              {app.forum.attribute('adsSponsoredLabelText') ||
                app.translator.trans('ralkage-ad-management.forum.ad.sponsored')}
            </div>
          )}
        </div>
      </div>
    );
  }

  image(ad) {
    const img = (
      <img
        className="AdBanner-image"
        src={ad.imageUrl}
        alt={ad.altText || ''}
        width={ad.width || undefined}
        height={ad.height || undefined}
      />
    );

    if (!ad.linkUrl) return img;

    return (
      <a
        className="AdBanner-link"
        href={ad.linkUrl}
        target="_blank"
        rel="noopener nofollow sponsored"
        onclick={() => {
          if (app.forum.attribute('adsTrackClicks')) track('/ad-track/click', { adId: ad.id });
        }}
      >
        {img}
      </a>
    );
  }

  // `html` and `adsense` ads are raw markup. innerHTML parses <script> but
  // never runs it, so each one is re-created in place -- src ones first, so
  // AdSense's library is in the document before the inline push.
  markup(ad) {
    return (
      <div
        className="AdBanner-html"
        oncreate={(vnode) => {
          vnode.dom.innerHTML = ad.content || '';

          Array.from(vnode.dom.querySelectorAll('script'))
            .sort((a, b) => (b.src ? 1 : 0) - (a.src ? 1 : 0))
            .forEach((dead) => {
              const script = document.createElement('script');
              Array.from(dead.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
              script.textContent = dead.textContent;
              dead.replaceWith(script);
            });
        }}
      />
    );
  }
}
